'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';

import { SCREENSHOT_SLIDES } from '@/constants/downloadApp';

import classes from '../../index.module.css';
import { useReveal, useRevealGroup } from '../hooks';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, EffectCoverflow } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

const LOOP_COPIES = 3;

const Screenshots = () => {
    const titleRef = useRevealGroup<HTMLDivElement>(classes.revealVisible);
    const swiperContainerRef = useReveal<HTMLDivElement>(classes.revealVisible);
    const swiperRef = useRef<SwiperType | null>(null);
    const [activeDot, setActiveDot] = useState(0);

    return (
        <section
            className='relative overflow-hidden px-4 py-16 md:py-24'
            style={{
                background:
                    'radial-gradient(800px 400px at 50% 0%, rgba(0,143,255,0.12), transparent 65%), linear-gradient(180deg, #040a1f, #0a1540 60%, #040a1f)'
            }}
            aria-labelledby='download-app-screenshots-title'>
            <div className='desktop-width md:px-8'>
                <div ref={titleRef} className='mx-auto mb-12 max-w-xl text-center'>
                    <span
                        data-reveal
                        className={`${classes.reveal} ${classes.glassDark} mb-3 inline-block rounded-lg px-4 py-1.5 text-xs font-bold text-white`}>
                        تصاویر اپلیکیشن ازکی‌سرویس
                    </span>
                    <h2
                        id='download-app-screenshots-title'
                        data-reveal
                        className={`${classes.reveal} text-2xl font-black text-white md:text-4xl md:leading-[1.4]`}
                        style={{ '--reveal-delay': '80ms' } as React.CSSProperties}>
                        سفارش، پیگیری و پرداخت؛ همه در یک اپ
                    </h2>
                    <p
                        data-reveal
                        className={`${classes.reveal} mt-4 text-sm leading-8 text-whiteMediumEmphasis md:text-base`}
                        style={{ '--reveal-delay': '160ms' } as React.CSSProperties}>
                        از انتخاب خدمت تا رسیدن کارشناس را لحظه‌به‌لحظه در اپلیکیشن ببینید و مدیریت کنید.
                    </p>
                </div>

                <div ref={swiperContainerRef} className={classes.reveal}>
                    <Swiper
                        dir='rtl'
                        modules={[EffectCoverflow, Autoplay]}
                        effect='coverflow'
                        grabCursor
                        centeredSlides
                        loop
                        loopAdditionalSlides={SCREENSHOT_SLIDES.length}
                        slidesPerView='auto'
                        coverflowEffect={{ rotate: 0, stretch: 0, depth: 160, modifier: 1.2, slideShadows: false }}
                        autoplay={{ delay: 3200, disableOnInteraction: false, pauseOnMouseEnter: true }}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        onSlideChange={(swiper) => setActiveDot(swiper.realIndex % SCREENSHOT_SLIDES.length)}>
                        {Array.from({ length: LOOP_COPIES }).flatMap((_, copy) =>
                            SCREENSHOT_SLIDES.map((slide) => (
                                <SwiperSlide key={`${slide.id}-${copy}`} className='!w-[220px] md:!w-[260px]'>
                                    <div
                                        className='relative aspect-[9/16] w-[220px] overflow-hidden rounded-lg
                                            shadow-[0_24px_60px_-16px_rgba(0,80,200,0.5)] md:w-[260px]'>
                                        <Image
                                            src={slide.src}
                                            alt={copy === 0 ? slide.alt : ''}
                                            aria-hidden={copy !== 0}
                                            fill
                                            sizes='(max-width: 768px) 220px, 260px'
                                            className='object-cover object-top'
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>

                    <div className={classes.pager} role='tablist' aria-label='انتخاب تصویر'>
                        {SCREENSHOT_SLIDES.map((slide, idx) => (
                            <button
                                key={slide.id}
                                type='button'
                                role='tab'
                                aria-selected={activeDot === idx}
                                aria-label={slide.alt}
                                onClick={() => swiperRef.current?.slideToLoop(idx)}
                                className={`${classes.pagerDot}${activeDot === idx ? ` ${classes.pagerDotActive}` : ''}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Screenshots;
